import { MentionItem } from 'react-mentions'

const MENTION_MARKUP = /@\[(.+?)\]\((.+?)\)/g

interface ParsedMention {
  id: string
  display: string
  plainTextStart: number
  plainTextEnd: number
  markupExtraLength: number
}

const parseMentions = (value: string): ParsedMention[] => {
  let markupExtraBefore = 0

  return Array.from(value.matchAll(MENTION_MARKUP), match => {
    const [markup, display, id] = match
    const plainTextStart = match.index - markupExtraBefore
    const markupExtraLength = markup.length - display.length
    markupExtraBefore += markupExtraLength

    return {
      id,
      display,
      plainTextStart,
      plainTextEnd: plainTextStart + display.length,
      markupExtraLength,
    }
  })
}

const toPlainText = (value: string) => value.replace(MENTION_MARKUP, '$1')

interface FindChangedRangeParams {
  before: string
  after: string
}

const findChangedRange = ({ before, after }: FindChangedRangeParams) => {
  const maxCommon = Math.min(before.length, after.length)

  let prefix = 0
  while (prefix < maxCommon && before[prefix] === after[prefix]) prefix++

  let suffix = 0
  while (
    suffix < maxCommon - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix++
  }

  return { start: prefix, end: before.length - suffix, insertedEnd: after.length - suffix }
}

interface ApplyPlainTextEditParams {
  value: string
  plainText: string
}

export const applyPlainTextEdit = ({ value, plainText }: ApplyPlainTextEditParams) => {
  const mentions = parseMentions(value)
  const change = findChangedRange({ before: toPlainText(value), after: plainText })

  const editedMentions = mentions.filter(
    mention => mention.plainTextStart < change.end && mention.plainTextEnd > change.start,
  )
  const start = Math.min(change.start, ...editedMentions.map(mention => mention.plainTextStart))
  const end = Math.max(change.end, ...editedMentions.map(mention => mention.plainTextEnd))
  const insertedEnd = change.insertedEnd + end - change.end

  const toMarkupIndex = (plainTextIndex: number) =>
    mentions
      .filter(mention => mention.plainTextEnd <= plainTextIndex)
      .reduce((index, mention) => index + mention.markupExtraLength, plainTextIndex)

  const newValue =
    value.slice(0, toMarkupIndex(start)) +
    plainText.slice(start, insertedEnd) +
    value.slice(toMarkupIndex(end))

  const newMentions: MentionItem[] = parseMentions(newValue).map(({ id, display }) => ({
    id,
    display,
    type: null,
  }))

  return { value: newValue, plainText: toPlainText(newValue), mentions: newMentions }
}
